import type { Moment } from "moment";
import moment from "moment";
import { getCityStateCountry } from "./addressUtils";
import { formatArrivalTime } from "./flightUtils";
import type { ShowTimeDisplay } from "./showDateTimeDisplay";

export interface EventHeaderTexts {
  tourName: string | undefined;
  title: string;
  subtitle: string | undefined;
  location: string | undefined;
}

// The event name takes the title spot, falling back to the venue and then the tour.
// The venue only moves to the subtitle when the event name is shown as title.
export const getShowHeaderTexts = (show: server.dto.Show): EventHeaderTexts => {
  const eventName = show.eventName?.trim() ? show.eventName : undefined;
  const venueName = show.venue?.name?.trim() ? show.venue.name : undefined;

  return {
    tourName: show.tour?.name,
    title: eventName ?? venueName ?? show.tour?.name ?? "...",
    subtitle: eventName && venueName ? venueName : undefined,
    location: show.venue ? getCityStateCountry(show.venue.address) || undefined : undefined
  };
};

export const getExternalEventHeaderTitle = (externalEvent: server.dto.ExternalEvent): string =>
  externalEvent.summary.trim() ? externalEvent.summary : "...";

// Reduces the show time model to a single line. The fallback time and a range that
// ends on a later day both render the same way in the header.
export const formatShowTimeLabel = (time: ShowTimeDisplay): string => {
  if (time.kind === "none") return "";
  if (time.kind === "fallback") return time.text;
  if (time.kind === "single") return time.start;
  return `${time.start}-${time.end}`;
};

// When the end day differs from the start day the header shows the offset (e.g. "+1")
// next to the end time instead of the full end date.
export const formatDayOffset = (start: Date | string | Moment | undefined, end: Date | string | Moment | undefined): string | undefined => {
  if (!start || !end) return undefined;
  const [isSameDay, timeDiffInDays, sign] = formatArrivalTime(moment.utc(start), moment.utc(end));
  return isSameDay ? undefined : `${sign}${timeDiffInDays}`;
};
